<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section duration-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <DashedCard
                :title="$t('serie.step2.renewal_period')"
                :title-size="15"
                :marginTop="37"
                :marginBottom="37"
                min-height="150px"
                :custom-style="{ padding: normalizeSize(24, true) }"
            >
                <template #content>
                    <div
                        class="gestion_creneau ppv2-flex ppv2-flex-wrap ppv2-flex-col lg:ppv2-flex-row ppv2-items-center ppv2-border ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mb-5 ppv2-justify-between ppv2-gap-2"
                        :style="{ padding: '10px' }"
                    >
                        <label
                            class="ppv2-text-black poppinsRegular"
                            :style="{
                                fontSize: normalizeFontSize(14),
                                width: 'calc(100% - ' + normalizeSize(175, true) + ')',
                                minWidth: 'max-content',
                            }"
                        >
                            Durée de l’abonnement
                        </label>
                        <div :style="{ width: normalizeSize(160, true) }">
                            <BaseSelect
                                :compact="true"
                                v-model="parameters.interval_hours_type"
                                :options="intervalOptions"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('serie.step3.select')"
                                class="transparent-select"
                                :error="errors.interval_hours_type"
                                @input="clearError('interval_hours_type')"
                                class-input-add="ppv2-min-h-[30px] ppv2-h-[30px] ppv2-rounded-lg"
                            />
                        </div>
                    </div>
                    <div
                        class="gestion_creneau ppv2-flex ppv2-flex-wrap ppv2-flex-col lg:ppv2-flex-row ppv2-items-center ppv2-border ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mb-5 ppv2-justify-between ppv2-gap-2"
                        :style="{ padding: '10px' }"
                    >
                        <label
                            class="ppv2-text-black poppinsRegular"
                            :style="{
                                fontSize: normalizeFontSize(14),
                                width: 'calc(100% - ' + normalizeSize(175, true) + ')',
                                minWidth: 'max-content',
                            }"
                        >
                            {{ $t('serie.step2.end_date_optional') }}
                        </label>
                        <div :style="{ width: normalizeSize(160, true) }">
                            <BaseDateInput v-model="pricing.expired_at" :customSizeLabel="12" />
                        </div>
                    </div>

                    <div
                        class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-text-black ppv2-justify-between ppv2-gap-2"
                        :style="{ padding: '10px' }"
                    >
                        <BaseSwitch
                            v-model="pricing.monthly_payment"
                            :label="$t('serie.step2.auto_renewal')"
                            :error="errors.monthly_payment"
                            @input="clearError('monthly_payment')"
                        />
                    </div>
                    <div
                        class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-text-black ppv2-justify-between ppv2-gap-2"
                        :style="{ padding: '10px' }"
                    >
                        <BaseSwitch
                            v-model="parameters.prestations_cumulated"
                            :label="$t('serie.step2.accumulate_prestations')"
                            :error="errors.prestations_cumulated"
                            @input="clearError('prestations_cumulated')"
                        />
                    </div>
                </template>
            </DashedCard>

            <DashedCard
                :title="$t('serie.step2.included_usages')"
                :title-size="15"
                :marginTop="37"
                :marginBottom="37"
                min-height="150px"
                :custom-style="{ padding: normalizeSize(24, true) }"
            >
                <template #content>
                    <div class="ppv2-flex ppv2-items-center ppv2-text-black ppv2-justify-between ppv2-gap-2">
                        <BaseSwitch
                            v-model="pricing.unlimited"
                            :label="$t('serie.step2.limit_total_usages')"
                            class="ppv2-mt-4"
                            :custom-style="{ width: 'calc(100% - ' + normalizeSize(115, true) + ')' }"
                            @focus="activeField = 'parameters'"
                            :error="errors.unlimited"
                            @input="clearError('unlimited')"
                        />
                        <div v-if="pricing.unlimited" class="ppv2-mr-3" :style="{ width: normalizeSize(100, true) }">
                            <CustomInput
                                v-model="pricing.prestations_qty"
                                type="number"
                                :min="0"
                                classInputAdd="!ppv2-min-h-[34px] !ppv2-h-[34px] mb-2 !ppv2-rounded-lg "
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                @focus="activeField = 'parameters'"
                                :state="getState('prestations_qty')"
                                @input="clearError('prestations_qty')"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-text-black ppv2-justify-between ppv2-gap-2">
                        <BaseSwitch
                            v-model="parameters.toggle_peak_hours"
                            :label="$t('serie.step2.limit_peak_hours')"
                            class="ppv2-mt-4"
                            :custom-style="{ width: 'calc(100% - ' + normalizeSize(115, true) + ')' }"
                            @focus="activeField = 'parameters'"
                            :error="errors.toggle_peak_hours"
                            @input="clearError('toggle_peak_hours')"
                        />
                        <div v-if="parameters.toggle_peak_hours" class="ppv2-mr-3" :style="{ width: normalizeSize(100, true) }">
                            <CustomInput
                                v-model="parameters.total_peak_hours"
                                type="number"
                                :min="0"
                                classInputAdd="!ppv2-min-h-[34px] !ppv2-h-[34px] mb-2 !ppv2-rounded-lg "
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                @focus="activeField = 'parameters'"
                                :state="getState('total_peak_hours')"
                                @input="clearError('total_peak_hours')"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-text-black ppv2-justify-between ppv2-gap-2">
                        <BaseSwitch
                            v-model="parameters.toggle_off_peak_hours"
                            :label="$t('serie.step2.limit_off_peak_hours')"
                            class="ppv2-mt-4"
                            :custom-style="{ width: 'calc(100% - ' + normalizeSize(115, true) + ')' }"
                            @focus="activeField = 'parameters'"
                            :error="errors.toggle_off_peak_hours"
                            @input="clearError('toggle_off_peak_hours')"
                        />
                        <div
                            v-if="parameters.toggle_off_peak_hours"
                            class="ppv2-mr-3"
                            :style="{ width: normalizeSize(100, true) }"
                        >
                            <CustomInput
                                v-model="parameters.total_off_peak_hours"
                                type="number"
                                :min="0"
                                classInputAdd="!ppv2-min-h-[34px] !ppv2-h-[34px] mb-2 !ppv2-rounded-lg "
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                @focus="activeField = 'parameters'"
                                :state="getState('total_off_peak_hours')"
                                @input="clearError('total_off_peak_hours')"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-text-black ppv2-justify-between ppv2-gap-2">
                        <BaseSwitch
                            v-model="parameters.allow_peak_for_off_peak"
                            :label="$t('serie.step2.allow_peak_for_off_peak')"
                            class="ppv2-mt-4"
                            @focus="activeField = 'parameters'"
                            :error="errors.allow_peak_for_off_peak"
                            @input="clearError('allow_peak_for_off_peak')"
                        />
                    </div>
                </template>
            </DashedCard>

            <SelectableList
                :label="$t('experience.step4.reservationMode.Question')"
                v-model="pricing.is_ticket"
                :options="options_tickets"
                class="ppv2-mb-[40px]"
            />

            <div
                v-if="!isOn"
                class="ppv2-relative"
                :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
            >
                <BaseSwitch v-model="isOn" :label="$t('serie.step2.limit_usage_rate')" />
            </div>
            <DashedCard
                v-else
                :marginTop="37"
                :marginBottom="37"
                min-height="150px"
                :custom-style="{ padding: normalizeSize(24, true), justifyContent: 'center' }"
            >
                <template #title>
                    <BaseSwitch v-model="isOn" :label="$t('serie.step2.limit_usage_rate')" />
                </template>
                <template #content>
                    <div
                        class="gestion_creneau ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-border ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mt-5 ppv2-mb-5 ppv2-justify-between"
                    >
                        <label
                            class="ppv2-text-black poppinsRegular"
                            :style="{
                                fontSize: normalizeFontSize(14),
                                width: 'calc(100% - ' + normalizeSize(195, true) + ')',
                                minWidth: 'max-content',
                            }"
                        >
                            {{ $t('serie.step2.per_period') }}
                        </label>

                        <div class="ppv2-mr-3" :style="{ width: normalizeSize(180, true) }">
                            <BaseSelect
                                :compact="true"
                                v-model="pricing.duree"
                                :options="durationOptions"
                                :required="true"
                                :error="errors.duree"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Sélectionner la durée..."
                                class="transparent-select"
                                class-added="!ppv2-h-[34px] !ppv2-rounded-lg"
                                @input="clearError('duree')"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-text-black ppv2-mb-2 ppv2-justify-between">
                        <BaseSwitch
                            v-model="parameters.toggle_max_peak_hours"
                            :label="$t('serie.step2.limit_max_peak_per_period')"
                            customClass="ppv2-max-w-[calc(100% - 150px)]"
                            :error="errors.toggle_max_peak_hours"
                            @input="clearError('toggle_max_peak_hours')"
                        />
                        <div v-if="parameters.toggle_max_peak_hours" class="ppv2-w-[150px]">
                            <CustomInput
                                v-model="parameters.nb_peak_hours_per"
                                type="number"
                                :min="0"
                                classInputAdd="!ppv2-min-h-[34px] !ppv2-h-[34px] mb-2 !ppv2-rounded-lg "
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :state="getState('nb_peak_hours_per')"
                                @input="clearError('nb_peak_hours_per')"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-text-black ppv2-mb-2 ppv2-justify-between">
                        <BaseSwitch
                            v-model="parameters.toggle_max_off_peak_hours"
                            :label="$t('serie.step2.limit_max_off_peak_per_period')"
                            customClass="ppv2-max-w-[calc(100% - 150px)]"
                            :error="errors.toggle_max_off_peak_hours"
                            @input="clearError('toggle_max_off_peak_hours')"
                        />
                        <div v-if="parameters.toggle_max_off_peak_hours" class="ppv2-w-[150px]">
                            <CustomInput
                                v-model="parameters.nb_off_peak_hours_per"
                                type="number"
                                :min="0"
                                classInputAdd="!ppv2-min-h-[34px] !ppv2-h-[34px] mb-2 !ppv2-rounded-lg "
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :state="getState('nb_off_peak_hours_per')"
                                @input="clearError('nb_off_peak_hours_per')"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>
                    </div>
                </template>
            </DashedCard>

            <!-- Prestations List (Integrated from Step 4) -->
            <div class="ppv2-mb-6">
                <DashedCard
                    :title="$t('serie.step2.limit_access_experiences')"
                    :title-size="15"
                    :marginTop="37"
                    :marginBottom="37"
                    :custom-style="{ padding: normalizeSize(24, true) }"
                >
                    <template #content>
                        <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-w-full">
                            <div v-if="seriePrestations.length > 0" class="ppv2-space-y-4 ppv2-mb-6 ppv2-w-full">
                                <div
                                    v-for="(item, index) in seriePrestations"
                                    :key="index"
                                    class="prestation-item-container ppv2-flex ppv2-items-center ppv2-relative"
                                    draggable="true"
                                    @dragstart="onDragStart($event, index)"
                                    @dragover="onDragOver($event)"
                                    @drop="onDrop($event, index)"
                                    @dragenter="onDragEnter($event)"
                                    @dragleave="onDragLeave($event)"
                                    @dragend="onDragEnd($event)"
                                >
                                    <GripVertical
                                        :size="16"
                                        color="#9D96A8"
                                        class="ppv2-cursor-grab ppv2-absolute ppv2-left-[-20px]"
                                    />
                                    <div class="prestation-item ppv2-border ppv2-rounded-lg ppv2-p-4 ppv2-flex-1">
                                        <div
                                            class="ppv2-flex ppv2-flex-wrap ppv2-items-start ppv2-w-full"
                                            :style="{ gap: normalizeSize(16, true) }"
                                        >
                                            <!-- Prestation Selection -->
                                            <div class="ppv2-flex-1">
                                                <BaseSelect
                                                    :compact="true"
                                                    :label="$t('serie.step2.choice') + ' ' + (index + 1)"
                                                    v-model="item.selectedPrestation"
                                                    :options="getAvailablePrestations(index)"
                                                    optionLabel="name"
                                                    :reduce="(opt) => opt"
                                                    :placeholder="$t('serie.step2.select_activity')"
                                                    :searchable="true"
                                                    :customSizeLabel="12"
                                                    @input="
                                                        onPrestationChange(index, $event)
                                                        clearError('prestations')
                                                    "
                                                    class="transparent-select ppv2-w-full"
                                                    class-added="!ppv2-h-[34px] mb-2 !ppv2-rounded-lg !ppv2-mb-2"
                                                    @open="activeField = 'prestations'"
                                                    @close="activeField = null"
                                                    :floatingLabel="true"
                                                    :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                                                />
                                            </div>

                                            <!-- Duration Selection -->
                                            <div class="ppv2-w-40">
                                                <BaseSelect
                                                    :compact="true"
                                                    label="Option"
                                                    :searchable="false"
                                                    :customSizeLabel="12"
                                                    v-model="item.selectedDuration"
                                                    :options="getAvailableDurations(item.selectedPrestation)"
                                                    :option-label="(option) => formatDuration(option)"
                                                    :reduce="(opt) => opt"
                                                    :placeholder="$t('serie.step2.duration')"
                                                    :disabled="!item.selectedPrestation"
                                                    class="transparent-select"
                                                    class-added="!ppv2-h-[34px] mb-2 !ppv2-rounded-lg !ppv2-mb-2"
                                                    @input="
                                                        onDurationChange(index, $event)
                                                        clearError('prestations')
                                                    "
                                                    @open="activeField = 'prestations'"
                                                    @close="activeField = null"
                                                    :floatingLabel="true"
                                                    :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Remove Button -->
                                    <div
                                        class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex align-items-center justify-center px-2"
                                        :style="{ width: '26px', marginLeft: normalizeSize(14), height: '26px' }"
                                    >
                                        <button
                                            type="button"
                                            @click="removeItem(index)"
                                            class="transparent-btn ppv2-text-black ppv2-flex ppv2-items-center ppv2-justify-center"
                                            title="Supprimer"
                                        >
                                            <Minus :size="15" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Add Item Button -->
                            <div
                                v-if="seriePrestations.length < maxItems"
                                class="ppv2-flex ppv2-justify-center"
                                :style="{ marginBottom: normalizeSizeVertical(10) }"
                            >
                                <BaseButton type="button" variant="secondary" :customFontSize="12" @click="addNewItem">
                                    <span class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                        <Plus :size="12" />
                                        {{ $t('serie.step2.add_activity') }}
                                    </span>
                                </BaseButton>
                            </div>
                        </div>
                    </template>
                </DashedCard>
            </div>

            <!-- Validation Error -->
            <div v-if="errors.prestations" class="error-text ppv2-mb-4">
                {{ errors.prestations }}
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': complited }">
                <BackButton v-show="!complited" type="button" @click="handleBackClick" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ complited ? $t('common.save') : $t('experience.next') }}
                </BaseButton>
            </div>
        </div>

        <!-- Exit Popup -->
        <CustomPopup v-model="showExitPopup" :defaultTitle="popupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="handleBackLeave"
                    :customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isSaveBtn="true"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="button"
                    variant="primary"
                    @click="showExitPopup = false"
                    :customHeight="43"
                    customClass="!ppv2-min-w-[250px] !ppv2-min-h-[40px]"
                    :isSaveBtn="true"
                    :isLargeBtn="true"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomPopup>

        <!-- Preview Panel -->
        <PreviewSerie :active-field="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import HeaderStep from './HeaderStep.vue'
import PreviewSerie from './PreviewSerie.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import SelectableList from '../../../Shared/SelectableList.vue'
import CustomPopup from '../../../Shared/CustomDialog.vue'
import { Plus, Minus, GripVertical } from 'lucide-vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'

export default {
    name: 'SerieStep3',
    components: {
        HeaderStep,
        PreviewSerie,
        BaseButton,
        BackButton,
        DashedCard,
        BaseSwitch,
        BaseSelect,
        CustomInput,
        Plus,
        Minus,
        GripVertical,
        SelectableList,
        CustomPopup,
        BaseDateInput,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        complited: { type: [Boolean], default: false },
    },
    data() {
        return {
            errors: {},
            activeField: null,
            options_tickets: [
                {
                    label: this.$t('serie.step2.tickets.participant_label'),
                    value: true,
                    subtitle: this.$t('serie.step2.tickets.participant_subtitle'),
                },
                {
                    label: this.$t('serie.step2.tickets.space_label'),
                    value: false,
                    subtitle: this.$t('serie.step2.tickets.space_subtitle'),
                },
            ],
            isOn: true,
            maxItems: 20,
            draggedIndex: null,
            initialDataString: null,
            showExitPopup: false,
            popupTitle: this.$t('experience.titleClosePopup'),
        }
    },
    mounted() {
        this.initialDataString = this.getFormDataString()
    },
    computed: {
        ...mapGetters('serie', ['parameters', 'pricing', 'isEditMode', 'loading']),
        intervalOptions() {
            return [
                { label: this.$t('serie.step2.options.month'), value: 'month' },
                { label: this.$t('serie.step2.options.week'), value: 'week' },
            ]
        },
        durationOptions() {
            return [
                { label: this.$t('serie.step2.options.days_7'), value: 7 },
                { label: this.$t('serie.step2.options.days_15'), value: 15 },
                { label: this.$t('serie.step2.options.month_1'), value: 30 },
                { label: this.$t('serie.step2.options.month_2'), value: 60 },
                { label: this.$t('serie.step2.options.month_3'), value: 90 },
                { label: this.$t('serie.step2.options.month_4'), value: 120 },
                { label: this.$t('serie.step2.options.month_6'), value: 180 },
                { label: this.$t('serie.step2.options.year_1'), value: 365 },
            ]
        },
        parameters: {
            get() {
                return this.$store.getters['serie/parameters']
            },
            set(value) {
                this.$store.dispatch('serie/setParameters', value)
            },
        },
        seriePrestations: {
            get() {
                return this.$store.getters['serie/seriePrestations']
            },
            set(value) {
                this.$store.dispatch('serie/setSeriePrestations', value)
            },
        },
        pricing: {
            get() {
                return this.$store.getters['serie/pricing']
            },
            set(value) {
                this.$store.dispatch('serie/setPricing', value)
            },
        },
        hoursOptions() {
            return Array.from({ length: 100 }, (_, i) => ({
                label: `${i + 1}`,
                value: i + 1,
            }))
        },
    },
    watch: {
        'parameters.toggle_peak_hours'(val) {
            if (!val) {
                this.setParameters({
                    ...this.parameters,
                    total_peak_hours: null,
                })
            }
        },
        'parameters.toggle_off_peak_hours'(val) {
            if (!val) {
                this.setParameters({
                    ...this.parameters,
                    total_off_peak_hours: null,
                })
            }
        },
        'parameters.toggle_max_peak_hours'(val) {
            if (!val) {
                this.setParameters({
                    ...this.parameters,
                    nb_peak_hours_per: null,
                })
            }
        },
        'parameters.toggle_max_off_peak_hours'(val) {
            if (!val) {
                this.setParameters({
                    ...this.parameters,
                    nb_off_peak_hours_per: null,
                })
            }
        },
    },
    methods: {
        normalizeFontSize,
        ...mapActions('serie', [
            'setParameters',
            'saveSerie',
            'addSeriePrestation',
            'updateSeriePrestation',
            'removeSeriePrestation',
            'setSeriePrestations',
        ]),
        normalizeSize,
        normalizeSizeVertical,

        clearError(field) {
            if (this.errors && this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },

        getState(field) {
            if (this.errors && this.errors[field]) return 'error'
            if (this.pricing && this.pricing[field]) return 'valid'
            if (this.parameters && this.parameters[field]) return 'valid'
            if (field === 'prestations' && this.seriePrestations.length > 0) return 'valid'
            return 'default'
        },
        getAvailablePrestations(currentIndex) {
            return this.$store.getters['serie/getAvailablePrestations'](currentIndex)
        },

        getAvailableDurations(prestation) {
            if (!prestation) return []
            return prestation.prices_by_times || prestation.pricesByTimes || prestation.durations || []
        },

        formatDuration(option) {
            if (!option) return ''
            return `${option.duration} min`
        },

        addNewItem() {
            this.addSeriePrestation({
                selectedPrestation: null,
                selectedDuration: null,
                quantity: 1,
            })
        },

        removeItem(index) {
            this.removeSeriePrestation(index)
        },

        onPrestationChange(index, prestation) {
            this.updateSeriePrestation({
                index,
                data: {
                    selectedPrestation: prestation,
                    selectedDuration: null,
                },
            })
        },

        onDurationChange(index, duration) {
            this.activeField = 'prestations'
            this.updateSeriePrestation({
                index,
                data: { selectedDuration: duration },
            })
        },

        onQuantityChange(index, quantity) {
            this.updateSeriePrestation({
                index,
                data: { quantity: parseInt(quantity) || 1 },
            })
        },

        getFormDataString() {
            return JSON.stringify({
                interval_hours_type: this.parameters.interval_hours_type,
                expired_at: this.pricing.expired_at,
                monthly_payment: this.pricing.monthly_payment,
                prestations_cumulated: this.parameters.prestations_cumulated,
                unlimited: this.pricing.unlimited,
                prestations_qty: this.pricing.prestations_qty,
                toggle_peak_hours: this.parameters.toggle_peak_hours,
                total_peak_hours: this.parameters.total_peak_hours,
                toggle_off_peak_hours: this.parameters.toggle_off_peak_hours,
                total_off_peak_hours: this.parameters.total_off_peak_hours,
                is_ticket: this.pricing.is_ticket,
                duree: this.pricing.duree,
                toggle_max_peak_hours: this.parameters.toggle_max_peak_hours,
                nb_peak_hours_per: this.parameters.nb_peak_hours_per,
                toggle_max_off_peak_hours: this.parameters.toggle_max_off_peak_hours,
                nb_off_peak_hours_per: this.parameters.nb_off_peak_hours_per,
                allow_peak_for_off_peak: this.parameters.allow_peak_for_off_peak,
                seriePrestations: this.seriePrestations,
            })
        },

        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },

        resetInitialState() {
            this.initialDataString = this.getFormDataString()
        },

        revertChanges() {
            if (!this.initialDataString) return
            const data = JSON.parse(this.initialDataString)
            this.$store.dispatch('serie/setParameters', {
                interval_hours_type: data.interval_hours_type,
                prestations_cumulated: data.prestations_cumulated,
                toggle_peak_hours: data.toggle_peak_hours,
                total_peak_hours: data.total_peak_hours,
                toggle_off_peak_hours: data.toggle_off_peak_hours,
                total_off_peak_hours: data.total_off_peak_hours,
                toggle_max_peak_hours: data.toggle_max_peak_hours,
                nb_peak_hours_per: data.nb_peak_hours_per,
                toggle_max_off_peak_hours: data.toggle_max_off_peak_hours,
                nb_off_peak_hours_per: data.nb_off_peak_hours_per,
                allow_peak_for_off_peak: data.allow_peak_for_off_peak,
            })
            this.$store.dispatch('serie/setPricing', {
                expired_at: data.expired_at,
                monthly_payment: data.monthly_payment,
                unlimited: data.unlimited,
                prestations_qty: data.prestations_qty,
                is_ticket: data.is_ticket,
                duree: data.duree,
            })
            this.$store.dispatch('serie/setSeriePrestations', data.seriePrestations)
        },

        validateForm() {
            this.errors = {}

            if (this.seriePrestations.length === 0) {
                this.errors.prestations = this.$t('serie.step2.validation.add_one_activity')
                return false
            }

            const hasIncomplete = this.seriePrestations.some((item) => !item.selectedPrestation || !item.selectedDuration)

            if (hasIncomplete) {
                this.errors.prestations = this.$t('serie.step2.validation.complete_all_activities')
                return false
            }

            return true
        },

        handleBackClick() {
            if (this.hasChanges()) {
                this.showExitPopup = true
            } else {
                this.$emit('previous-step')
            }
        },

        handleBackLeave() {
            this.showExitPopup = false
            this.revertChanges()
            this.$emit('previous-step')
        },

        async submitForm() {
            // Local validation
            if (!this.validateForm()) {
                return
            }

            // Save to API with error handling
            try {
                await this.saveSerie()
                this.initialDataString = this.getFormDataString()
                this.$toast.success(
                    this.id ? this.$t('serie.notifications.success_update') : this.$t('serie.notifications.success_add')
                )
                if (!this.complited) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Display field-specific validation errors if present
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                }
            }
        },

        async saveStep(redirect = false) {
            try {
                await this.saveSerie()
                this.initialDataString = this.getFormDataString()
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving serie:', error)
            }
        },

        onDragStart(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this.draggedIndex = index
        },

        onDragOver(evt) {
            evt.preventDefault()
            evt.dataTransfer.dropEffect = 'move'
        },

        onDrop(evt, targetIndex) {
            evt.preventDefault()
            const fromIndex = this.draggedIndex
            const toIndex = targetIndex

            const container = evt.currentTarget.closest('.prestation-item-container')
            if (container) container.style.backgroundColor = ''

            if (fromIndex !== toIndex && fromIndex !== null) {
                const items = [...this.seriePrestations]
                const item = items.splice(fromIndex, 1)[0]
                items.splice(toIndex, 0, item)
                this.seriePrestations = items
            }
            this.draggedIndex = null
        },

        onDragEnter(evt) {
            const container = evt.currentTarget.closest('.prestation-item-container')
            if (container) container.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'
        },

        onDragLeave(evt) {
            const container = evt.currentTarget.closest('.prestation-item-container')
            if (container && !container.contains(evt.relatedTarget)) {
                container.style.backgroundColor = ''
            }
        },

        onDragEnd(evt) {
            this.draggedIndex = null
            const allItems = document.querySelectorAll('.prestation-item-container')
            allItems.forEach((item) => {
                item.style.backgroundColor = ''
            })
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}

.prestation-item {
    border-color: rgba(0, 0, 0, 0.1);
}

.dark .prestation-item {
    border-color: rgba(255, 255, 255, 0.1);
}

.border_btns {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
}

.dark .border_btns {
    border-color: rgba(255, 255, 255, 0.1);
}

.transparent-btn {
    background: transparent;
    border: none;
    cursor: pointer;
}
</style>
